import {
  isIntegrationProvider,
  type IntegrationProvider,
} from "@repo/app-types/entities/integration";
import { useEffect, useState, type FC } from "react";

export const OAUTH_INTEGRATION_CALLBACK_QUERY_PARAM_NAME =
  "oauthIntegrationCallback";

const INTEGRATION_CONNECTED_CHANNEL_NAME = "alpharun:integration-connected";

const subscribers = new Set<() => void>();

export interface IntegrationConnectedMessage {
  provider: IntegrationProvider;
}

/**
 * True when this window is the OAuth popup landing back on the app after the
 * provider's callback. The API redirects here rather than rendering its own
 * page so that the popup ends up on the app's origin, which is what lets it
 * announce the new integration on the BroadcastChannel below.
 */
export function getIsOAuthIntegrationCallback(): boolean {
  const searchParams = new URLSearchParams(window.location.search);
  return (
    searchParams.get(OAUTH_INTEGRATION_CALLBACK_QUERY_PARAM_NAME) === "true"
  );
}

/**
 * Announces a newly connected integration to the app. Deliberately not
 * postMessage-to-opener: the API sets `Cross-Origin-Opener-Policy: same-origin`
 * (helmet's default), which severs `window.opener` across the OAuth redirect, so
 * the popup has no handle on the window that opened it. A BroadcastChannel needs
 * no such handle — only a shared origin.
 */
export function subscribeToIntegrationConnected(
  onConnected: () => void,
): () => void {
  const channel = new BroadcastChannel(INTEGRATION_CONNECTED_CHANNEL_NAME);
  channel.onmessage = () => {
    onConnected();
  };
  subscribers.add(onConnected);
  return () => {
    subscribers.delete(onConnected);
    channel.close();
  };
}

/**
 * The manual path for when the broadcast is partitioned: notifies every
 * subscriber as if the popup had announced a new integration.
 */
export function refreshIntegrations(): void {
  subscribers.forEach((onConnected) => {
    onConnected();
  });
}

/**
 * Browsers partition BroadcastChannel in a frame under another top-level site,
 * so the popup's announcement never reaches us. A cross-origin top is the check
 * we can make; same-site cross-origin framing would be a false positive, which
 * we don't have.
 */
export function getIsIntegrationConnectedBroadcastPartitioned(): boolean {
  if (window.top === window.self) return false;
  try {
    return window.top?.location.origin !== window.location.origin;
  } catch {
    return true;
  }
}

export const OAuthPopupCallbackPage: FC = () => {
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const provider = searchParams.get("provider");
    const isSuccess = searchParams.get("status") === "success";

    if (!isSuccess || provider === null || !isIntegrationProvider(provider)) {
      setHasFailed(true);
      return;
    }

    const channel = new BroadcastChannel(INTEGRATION_CONNECTED_CHANNEL_NAME);
    channel.postMessage({ provider } satisfies IntegrationConnectedMessage);
    channel.close();

    window.close();
  }, []);

  return (
    <div className="p-4 text-center text-sm">
      {hasFailed
        ? "An error occurred while adding this integration. Please close this window and try again."
        : "Integration connected - you can close this window and return to Alpharun."}
    </div>
  );
};
